import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { galerieService, GalerieImage } from '../services/galerieService';

export const Galerie: React.FC = () => {
  const [images, setImages] = useState<GalerieImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [selectedImage, setSelectedImage] = useState<GalerieImage | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const categories = [
    { value: 'all', label: 'Toutes', icon: '📸' },
    { value: 'membres', label: 'Membres', icon: '👥' },
    { value: 'bureau', label: 'Bureau', icon: '👔' },
    { value: 'serigne', label: 'Serigne', icon: '🕌' },
    { value: 'khlifa', label: 'Khlifa', icon: '👑' },
    { value: 'autre', label: 'Autre', icon: '📌' },
  ];

  useEffect(() => {
    setCurrentPage(1);
    fetchImages(1);
  }, [selectedCategory, selectedSection]);

  const fetchImages = async (page: number = 1) => {
    setLoading(true);
    try {
      const filters: any = {};
      if (selectedCategory !== 'all') filters.categorie = selectedCategory;
      if (selectedSection !== 'all') filters.section = selectedSection;
      const data = await galerieService.getAll(page, 6, filters);
      setImages(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / 6));
    } catch (error) {
      console.error('Erreur:', error);
      setImages([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-white via-green-50/30 to-green-100/50 overflow-hidden">
      {/* Motifs décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative z-10 py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="relative inline-block bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-3xl px-8 py-8 shadow-2xl overflow-hidden group">
              {/* Effet de brillance animé */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              {/* Contenu */}
              <div className="relative z-10">
                <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg">
                  Galerie Photo
                </h1>
                <p className="text-green-50 text-xl font-medium">
                  Découvrez tous les moments forts de nos activités
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grille d'images */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-green-200/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-green-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pb-12 sm:pb-16 md:pb-24">
          {/* Layout latéral avec filtres et contenu */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* Sidebar filtres - seulement sur desktop */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50 sticky top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🔍 Filtres</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Section</h4>
                    <select
                      value={selectedSection}
                      onChange={(e) => {
                        setSelectedSection(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    >
                      <option value="all">Toutes les sections</option>
                      <option value="VCN">VCN (Université)</option>
                      <option value="HDR">HDR</option>
                      <option value="SANTE">SANTÉ</option>
                    </select>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Catégorie</h4>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => {
                        setSelectedCategory(cat.value);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-4 py-3 rounded-xl font-semibold transition-all duration-300 text-sm text-left ${
                        selectedCategory === cat.value
                          ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-green-50 border-2 border-gray-200'
                      }`}
                    >
                      <span className="mr-2">{cat.icon}</span>
                      {cat.label}
                    </button>
                  ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contenu principal - images */}
            <div className="lg:col-span-3">
              {/* Filtres mobiles - visible seulement sur mobile/tablet */}
              <div className="lg:hidden mb-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Section</label>
                    <select
                      value={selectedSection}
                      onChange={(e) => {
                        setSelectedSection(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    >
                      <option value="all">Toutes les sections</option>
                      <option value="VCN">VCN (Université)</option>
                      <option value="HDR">HDR</option>
                      <option value="SANTE">SANTÉ</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie</label>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {categories.map((cat) => (
                      <button
                        key={cat.value}
                        onClick={() => {
                          setSelectedCategory(cat.value);
                          setCurrentPage(1);
                        }}
                        className={`px-4 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm ${
                          selectedCategory === cat.value
                            ? 'bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg'
                            : 'bg-white text-gray-700 hover:bg-green-50 border-2 border-gray-200'
                        }`}
                      >
                        <span className="mr-2">{cat.icon}</span>
                        {cat.label}
                      </button>
                    ))}
                    </div>
                  </div>
                </div>
              </div>
              {loading ? (
                <div className="flex justify-center py-12 sm:py-16 md:py-20">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-700 border-t-transparent"></div>
                </div>
              ) : images.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <div
                key={image.id}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                onClick={async () => {
                  setSelectedImage(image);
                  setSelectedIndex(index);
                  // Incrémenter les vues
                  try {
                    await galerieService.incrementVue(image.id);
                    // Rafraîchir la liste pour mettre à jour les vues
                    fetchImages(currentPage);
                  } catch (error) {
                    console.error('Erreur lors de l\'incrémentation des vues:', error);
                  }
                }}
                style={{ animation: `zoomIn 0.6s ease-out`, animationDelay: `${index * 50}ms` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={image.image}
                    alt={image.titre}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg mb-1">{image.titre}</h3>
                      {image.description && (
                        <p className="text-sm text-gray-200 line-clamp-2">{image.description}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                      {image.categorie}
                    </span>
                    <span className="text-gray-400 text-xs">👁️ {image.nombre_vues}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-32">
            <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-green-100/50">
              <div className="text-8xl mb-6">📸</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucune image dans cette catégorie</h3>
              <p className="text-gray-600 text-lg">
                Essayez de sélectionner une autre catégorie
              </p>
            </div>
          </div>
        )}
        
        {/* Pagination Galerie */}
        {totalPages > 1 && (
          <div className="w-full flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 sm:space-x-2 mt-8 px-4">
            <button
              onClick={() => {
                const newPage = Math.max(1, currentPage - 1);
                setCurrentPage(newPage);
                fetchImages(newPage);
              }}
              disabled={currentPage === 1 || loading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              ← Précédent
            </button>
            <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0 max-w-full">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => {
                      setCurrentPage(pageNum);
                      fetchImages(pageNum);
                    }}
                    disabled={loading}
                    className={`px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                      currentPage === pageNum
                        ? 'bg-green-600 text-white shadow-lg scale-110'
                        : 'bg-white text-gray-700 hover:bg-green-50 border border-green-200'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => {
                const newPage = Math.min(totalPages, currentPage + 1);
                setCurrentPage(newPage);
                fetchImages(newPage);
              }}
              disabled={currentPage === totalPages || loading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Suivant →
            </button>
          </div>
        )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal Image améliorée */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center p-2 sm:p-4 animate-fade-in overflow-y-auto"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="max-w-6xl w-full bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl animate-zoom-in relative my-4 sm:my-0 max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
                    setSelectedIndex(prevIndex);
                    setSelectedImage(images[prevIndex]);
                    try {
                      await galerieService.incrementVue(images[prevIndex].id);
                      fetchImages(currentPage);
                    } catch (error) {
                      console.error('Erreur lors de l\'incrémentation des vues:', error);
                    }
                  }}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl transition-all duration-300 transform hover:scale-110"
                >
                  <span className="text-xl sm:text-2xl">←</span>
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const nextIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
                    setSelectedIndex(nextIndex);
                    setSelectedImage(images[nextIndex]);
                    try {
                      await galerieService.incrementVue(images[nextIndex].id);
                      fetchImages(currentPage);
                    } catch (error) {
                      console.error('Erreur lors de l\'incrémentation des vues:', error);
                    }
                  }}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl transition-all duration-300 transform hover:scale-110"
                >
                  <span className="text-xl sm:text-2xl">→</span>
                </button>
              </>
            )}

            {/* Image */}
            <div className="relative h-[50vh] sm:h-[60vh] md:h-[75vh] bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0">
              <img
                src={selectedImage.image}
                alt={selectedImage.titre}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Infos */}
            <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-gray-100 flex-shrink-0 overflow-y-auto">
              <div className="flex items-start justify-between mb-3 sm:mb-4 gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 break-words">{selectedImage.titre}</h2>
                  {selectedImage.description && (
                    <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-3 sm:mb-4 break-words">{selectedImage.description}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="w-8 h-8 sm:w-10 sm:h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-lg flex-shrink-0"
                >
                  <span className="text-sm sm:text-base">✕</span>
                </button>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 pt-3 sm:pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:space-x-4">
                  <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-5 py-1.5 sm:py-2 rounded-full font-semibold text-xs sm:text-sm whitespace-nowrap">
                    {categories.find(c => c.value === selectedImage.categorie)?.icon} {categories.find(c => c.value === selectedImage.categorie)?.label || selectedImage.categorie}
                  </span>
                  <span className="text-gray-600 flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base">
                    <span>👁️</span>
                    <span className="font-semibold">{selectedImage.nombre_vues} vues</span>
                  </span>
                  <span className="text-gray-400 text-xs sm:text-sm">
                    📅 {new Date(selectedImage.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                {images.length > 1 && (
                  <span className="text-gray-500 text-xs sm:text-sm font-medium">
                    {selectedIndex + 1} / {images.length}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

